import { useMemo } from 'react';
import {
  getAllAuthors,
  getAllCollections,
  getAllSeries,
  getAuthorBooks,
  getAuthorInfo,
  getRecentAuthors,
  getRecentSeries,
  getSeriesDetail,
} from '../api/abs';
import { progressStore } from '../api/progressStore';
import { getSession, sessionStore } from '../api/session';
import type { Collection, LibraryItem, Series } from '../api/types';
import { downloadsStore } from '../downloads/manager';
import { getQueryData, prefetchQuery, useQuery } from '../lib/query';
import { useStore } from '../lib/store';
import { activeLibraryStore, useEnsureActiveLibrary } from './activeLibrary';
import {
  authorFromAbs,
  bookFromLibraryItem,
  collectionFromAbs,
  downloadedIds,
  groupBookCount,
  isBookEffectivelyFinished,
  seriesFromAbs,
  useLiveBooks,
  type AuthorSummary,
  type BookSummary,
  type CollectionSummary,
  type GroupSummary,
  type SeriesSummary,
} from './models';
import { userScopeKey } from './scope';

// Series, Collections and Authors: list and detail loaders (spec 04 §9-10).
// Built on the query cache: cached data shows instantly and refreshes in the
// background once it is a minute old (Flutter's stale-while-revalidate;
// the 5-minute TTL only decided whether a reload showed a spinner, and with
// data on screen it never does). Every request is keyed by account +
// library, and results of superseded requests are dropped. Errors are
// returned, never turned into empty lists.

export const GROUP_LIST_STALE_MS = 60_000;
export const GROUP_RECENT_STALE_MS = 2 * 60_000;
export const GROUP_DETAIL_STALE_MS = 60_000;

export const NO_LIBRARY_MESSAGE = 'No audiobook library was found on this server.';
export const NO_LIBRARY_TITLE = 'No library found';

type ListKind = 'series' | 'collections' | 'authors' | 'recent-series' | 'recent-authors';

function listKey(kind: ListKind, libraryId: string, scope = userScopeKey()): string {
  return `groups:${kind}:${scope ?? ''}:${libraryId}`;
}

const fetchers = {
  series: (libraryId: string) => getAllSeries(libraryId).then((list) => list.map(seriesFromAbs)),
  collections: (libraryId: string) => getAllCollections(libraryId).then((list) => list.map(collectionFromAbs)),
  authors: (libraryId: string) => getAllAuthors(libraryId).then((list) => list.map(authorFromAbs)),
  'recent-series': (libraryId: string) => getRecentSeries(libraryId, 10).then((list) => list.map(seriesFromAbs)),
  'recent-authors': (libraryId: string) => getRecentAuthors(libraryId, 10).then((list) => list.map(authorFromAbs)),
} satisfies Record<ListKind, (libraryId: string) => Promise<unknown[]>>;

/** Warms the Recent Series / Newest Authors / Series / Collections caches (Flutter `prefetchHomeSections`). */
export function prefetchHomeSections(libraryId: string): void {
  if (!getSession()) return;
  prefetchQuery(listKey('recent-series', libraryId), () => fetchers['recent-series'](libraryId), { staleMs: GROUP_RECENT_STALE_MS });
  prefetchQuery(listKey('recent-authors', libraryId), () => fetchers['recent-authors'](libraryId), { staleMs: GROUP_RECENT_STALE_MS });
  prefetchQuery(listKey('series', libraryId), () => fetchers.series(libraryId), { staleMs: GROUP_LIST_STALE_MS });
  prefetchQuery(listKey('collections', libraryId), () => fetchers.collections(libraryId), { staleMs: GROUP_LIST_STALE_MS });
}

export interface GroupListResult<T> {
  items: T[];
  libraryId: string | null;
  /** No data yet and a request is running. */
  loading: boolean;
  /** A background refresh is running (thin progress bar). */
  refreshing: boolean;
  /** No data, the last attempt failed, and a new one (Retry) is running: show the loading state again. */
  retrying?: boolean;
  /** User-facing error (shown when there is no data). */
  error: string | null;
  errorDetail: unknown;
  /** The account has no usable audiobook library (or every library is disabled). */
  noLibrary: boolean;
  refresh: () => Promise<void>;
}

function messageOf(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error && err.message ? err.message : 'Something went wrong. Please try again.';
}

/** Series detail without a session (spec 04 §6.3). */
export const NOT_CONNECTED_MESSAGE = 'Not connected to server';
/** Author detail without a session or library (spec 04 §7.2). */
export const NO_AUTHOR_LIBRARY_MESSAGE = 'Not connected to server or library not selected';

/** Detail screens' fetch error, verbatim from Flutter: `Failed to load books: <reason>`. */
function booksErrorMessage(err: unknown): string | null {
  const message = messageOf(err);
  return message ? `Failed to load books: ${message}` : null;
}

function useGroupList<T>(kind: ListKind, staleMs: number): GroupListResult<T> {
  const session = useStore(sessionStore);
  const lib = useEnsureActiveLibrary();
  const libraryId = lib.libraryId;
  const key = session && libraryId ? listKey(kind, libraryId, userScopeKey(session)) : null;
  const q = useQuery<T[]>(key, () => (fetchers[kind] as (id: string) => Promise<T[]>)(libraryId as string), { staleMs });
  const resolving = !!session && !libraryId && (lib.status === 'idle' || lib.status === 'loading') && !lib.noEnabledLibraries;
  const noLibrary = !!session && !libraryId && !resolving;
  const libraryError = !libraryId && lib.status === 'error' ? lib.error : null;
  const items = useMemo(() => q.data ?? [], [q.data]);
  return {
    items,
    libraryId,
    loading: q.loading || resolving,
    refreshing: q.refreshing && q.data !== undefined,
    retrying: q.refreshing && q.data === undefined && q.error !== undefined,
    error: noLibrary ? (libraryError ? messageOf(libraryError) : NO_LIBRARY_MESSAGE) : messageOf(q.error),
    errorDetail: noLibrary ? libraryError : q.error,
    noLibrary: noLibrary && !libraryError,
    refresh: q.refresh,
  };
}

/** Series tab: every series, newest first. */
export function useSeriesList(): GroupListResult<SeriesSummary> {
  return useGroupList<SeriesSummary>('series', GROUP_LIST_STALE_MS);
}

/** Collections tab: every collection, newest first then title. */
export function useCollectionsList(): GroupListResult<CollectionSummary> {
  return useGroupList<CollectionSummary>('collections', GROUP_LIST_STALE_MS);
}

/** Authors tab: every author (server order; sort with `filterSortAuthors`). */
export function useAuthorsList(): GroupListResult<AuthorSummary> {
  return useGroupList<AuthorSummary>('authors', GROUP_LIST_STALE_MS);
}

/** Home "Recent Series": 10 newest (2-minute cache). */
export function useRecentSeries(): GroupListResult<SeriesSummary> {
  return useGroupList<SeriesSummary>('recent-series', GROUP_RECENT_STALE_MS);
}

/** Home "Newest Authors": 10 newest (2-minute cache). */
export function useNewestAuthors(): GroupListResult<AuthorSummary> {
  return useGroupList<AuthorSummary>('recent-authors', GROUP_RECENT_STALE_MS);
}

// ---------------------------------------------------------------- list filtering (pure)

export type SeriesSort = 'recentlyAdded' | 'titleAsc' | 'bookCountDesc';
export type SeriesStatusFilter = 'all' | 'finished' | 'unfinished';
export type AuthorSort = 'recentlyAdded' | 'nameAsc' | 'bookCountDesc';

const cmpText = (a: string, b: string): number => {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
};

/** Series / Collections visible list (spec 04 §4.3): title search, status filter, sort with tiebreaks. */
export function filterSortSeries<T extends GroupSummary>(list: readonly T[], opts: { query?: string; sort?: SeriesSort; status?: SeriesStatusFilter } = {}): T[] {
  const query = (opts.query ?? '').trim().toLowerCase();
  let result = query ? list.filter((s) => s.name.toLowerCase().includes(query)) : [...list];
  if (opts.status === 'finished') result = result.filter((s) => s.isFinished);
  else if (opts.status === 'unfinished') result = result.filter((s) => !s.isFinished);
  switch (opts.sort ?? 'recentlyAdded') {
    case 'recentlyAdded':
      return result.sort((a, b) => b.addedAt - a.addedAt || cmpText(a.name, b.name));
    case 'titleAsc':
      return result.sort((a, b) => cmpText(a.name, b.name) || b.addedAt - a.addedAt);
    case 'bookCountDesc':
      return result.sort((a, b) => groupBookCount(b) - groupBookCount(a) || cmpText(a.name, b.name));
  }
}

/** Authors visible list (spec 04 §5.2). */
export function filterSortAuthors(list: readonly AuthorSummary[], opts: { query?: string; sort?: AuthorSort } = {}): AuthorSummary[] {
  const query = (opts.query ?? '').trim().toLowerCase();
  const result = query ? list.filter((a) => a.name.toLowerCase().includes(query)) : [...list];
  switch (opts.sort ?? 'nameAsc') {
    case 'recentlyAdded':
      return result.sort((a, b) => b.addedAt - a.addedAt || cmpText(a.name, b.name));
    case 'nameAsc':
      return result.sort((a, b) => cmpText(a.name, b.name));
    case 'bookCountDesc':
      return result.sort((a, b) => b.numBooks - a.numBooks || cmpText(a.name, b.name));
  }
}

// ---------------------------------------------------------------- series progress and numbering (pure)

export interface SeriesProgress {
  finishedCount: number;
  totalCount: number;
  isComplete: boolean;
  /** Finished books / all books (in-progress books get no partial credit). */
  fraction: number;
}

/** Series detail header: "N of M books completed" / "Series Complete". */
export function seriesProgress(books: readonly BookSummary[]): SeriesProgress {
  const totalCount = books.length;
  const finishedCount = books.filter((b) => isBookEffectivelyFinished(b)).length;
  return { finishedCount, totalCount, isComplete: totalCount > 0 && finishedCount === totalCount, fraction: totalCount > 0 ? finishedCount / totalCount : 0 };
}

/**
 * `#n` badge (Flutter `seriesNumber`): position in the list payload's
 * `libraryItemIds`, else grid position. ABS `sequence` is not used.
 */
export function seriesNumber(bookId: string, gridIndex: number, libraryItemIds: readonly string[]): number {
  const i = libraryItemIds.indexOf(bookId);
  return i >= 0 ? i + 1 : gridIndex + 1;
}

// ---------------------------------------------------------------- detail screens

function toBooks(items: LibraryItem[]): BookSummary[] {
  const progress = progressStore.get();
  const downloaded = downloadedIds(downloadsStore.get());
  return items.map((i) => bookFromLibraryItem(i, { progress: progress[i.id], isDownloaded: downloaded.has(i.id) }));
}

/** A series / collection already loaded by a list (title, `libraryItemIds`), if any. */
export function findCachedGroup(id: string, libraryId: string | null = activeLibraryStore.get().libraryId): GroupSummary | undefined {
  if (!libraryId) return undefined;
  const scope = userScopeKey();
  for (const kind of ['series', 'collections', 'recent-series'] as const) {
    const hit = getQueryData<GroupSummary[]>(listKey(kind, libraryId, scope))?.find((g) => g.id === id);
    if (hit) return hit;
  }
  return undefined;
}

export interface GroupDetailResult {
  /** Series or collection record (from the detail request, or the list that opened it). */
  group: GroupSummary | null;
  /** Books in server order with live progress. */
  books: BookSummary[];
  progress: SeriesProgress;
  /** Ids used for `#n` badges. */
  libraryItemIds: string[];
  loading: boolean;
  refreshing: boolean;
  /** No data, the last attempt failed, and a new one (Retry) is running. */
  retrying?: boolean;
  error: string | null;
  errorDetail: unknown;
  refresh: () => Promise<void>;
}

interface SeriesDetailData {
  group: GroupSummary | null;
  books: BookSummary[];
}

/**
 * Series or collection detail (`col_` ids or `kind: 'collection'` use the
 * collections endpoint). Pass the list's `libraryItemIds` for numbering and
 * as the per-item fallback.
 */
export function useSeriesDetail(id: string | null, opts: { kind?: 'series' | 'collection'; seed?: GroupSummary | null } = {}): GroupDetailResult {
  const session = useStore(sessionStore);
  const seed = opts.seed ?? (id ? (findCachedGroup(id) ?? null) : null);
  const fallbackIds = seed?.libraryItemIds ?? [];
  const key = session && id ? `groups:detail:${userScopeKey(session)}:${opts.kind ?? 'auto'}:${id}` : null;
  const q = useQuery<SeriesDetailData>(
    key,
    async () => {
      const detail = await getSeriesDetail(id as string, fallbackIds, opts.kind);
      const group = detail.group ? (detail.kind === 'collection' ? collectionFromAbs(detail.group as Collection) : seriesFromAbs(detail.group as Series)) : null;
      return { group, books: toBooks(detail.items) };
    },
    { staleMs: GROUP_DETAIL_STALE_MS },
  );
  const books = useLiveBooks(q.data?.books ?? EMPTY_BOOKS);
  const fetched = q.data?.group ?? null;
  const libraryItemIds = seed?.libraryItemIds.length ? seed.libraryItemIds : (fetched?.libraryItemIds ?? EMPTY_IDS);
  const group = useMemo(() => {
    const base = seed ?? fetched;
    return base ? { ...base, libraryItemIds } : null;
  }, [seed, fetched, libraryItemIds]);
  const progress = useMemo(() => seriesProgress(books), [books]);
  const notConnected = !session && !!id;
  return {
    group,
    books,
    progress,
    libraryItemIds,
    loading: q.loading,
    refreshing: q.refreshing && q.data !== undefined,
    retrying: q.refreshing && q.data === undefined && q.error !== undefined,
    error: notConnected ? NOT_CONNECTED_MESSAGE : booksErrorMessage(q.error),
    errorDetail: notConnected ? null : q.error,
    refresh: q.refresh,
  };
}

const EMPTY_BOOKS: BookSummary[] = [];
const EMPTY_IDS: string[] = [];

export interface AuthorDetailResult {
  author: AuthorSummary | null;
  /** The author's books, newest first, with live progress. */
  books: BookSummary[];
  loading: boolean;
  refreshing: boolean;
  /** No data, the last attempt failed, and a new one (Retry) is running. */
  retrying?: boolean;
  error: string | null;
  errorDetail: unknown;
  refresh: () => Promise<void>;
}

/** Author detail: the author record (seed, list cache or server) and all their books in the active library. */
export function useAuthorDetail(authorId: string | null, opts: { seed?: AuthorSummary | null } = {}): AuthorDetailResult {
  const session = useStore(sessionStore);
  const lib = useEnsureActiveLibrary();
  const libraryId = lib.libraryId;
  const scope = session ? userScopeKey(session) : null;
  const fromList =
    opts.seed ??
    (libraryId && authorId
      ? (getQueryData<AuthorSummary[]>(listKey('authors', libraryId, scope))?.find((a) => a.id === authorId) ??
        getQueryData<AuthorSummary[]>(listKey('recent-authors', libraryId, scope))?.find((a) => a.id === authorId))
      : undefined);
  const info = useQuery<AuthorSummary>(fromList || !authorId || !scope ? null : `groups:author:${scope}:${authorId}`, () => getAuthorInfo(authorId!).then(authorFromAbs), {
    staleMs: GROUP_DETAIL_STALE_MS,
  });
  const booksQuery = useQuery<BookSummary[]>(
    scope && libraryId && authorId ? `groups:author-books:${scope}:${libraryId}:${authorId}` : null,
    () => getAuthorBooks(libraryId!, authorId!).then(toBooks),
    { staleMs: GROUP_DETAIL_STALE_MS },
  );
  const books = useLiveBooks(booksQuery.data ?? EMPTY_BOOKS);
  const resolving = !!session && !libraryId && (lib.status === 'idle' || lib.status === 'loading');
  const noLibrary = !!authorId && !resolving && (!session || !libraryId);
  return {
    author: fromList ?? info.data ?? null,
    books,
    loading: booksQuery.loading || resolving,
    refreshing: booksQuery.refreshing && booksQuery.data !== undefined,
    retrying: booksQuery.refreshing && booksQuery.data === undefined && booksQuery.error !== undefined,
    error: noLibrary ? NO_AUTHOR_LIBRARY_MESSAGE : booksErrorMessage(booksQuery.error),
    errorDetail: noLibrary ? null : booksQuery.error,
    refresh: async () => {
      await Promise.all([booksQuery.refresh(), info.refresh()]);
    },
  };
}
